import { head, uniq } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { SectionInfo } from 'src/WebApiController';

export const useAutoCompMapSectionHandler = (
  onLaunchingDialog: () => void,
  disabled = false
) => {
  const { venueMapInfo } = useEventMapContext();
  const [selectedSection, setSectionSelected] = useState<SectionInfo | null>(
    null
  );

  const [selectedSectionIds, setSelectedSectionIds] = useState<number[]>([]);

  const selectedSectionData = useMemo(() => {
    if (!venueMapInfo || !selectedSectionIds?.length) {
      return undefined;
    }

    return venueMapInfo?.sections?.filter((s: SectionInfo) =>
      selectedSectionIds.includes(s.id)
    );
  }, [selectedSectionIds, venueMapInfo]);

  const onResetSections = useCallback(() => setSelectedSectionIds([]), []);

  const onShiftSelect = useCallback(
    (sectionInfo: SectionInfo) => {
      if (disabled) return;
      // We are using the ticketClassId to group the sections
      const selectedClassId = head(
        sectionInfo?.rows
          ?.map(({ tktClass: ticketClass }) => ticketClass?.id)
          .filter((id) => !!id)
      );
      const updatedSectionIds = venueMapInfo?.sections
        ?.filter(({ rows }: SectionInfo) =>
          rows?.some(
            ({ tktClass: ticketClass }) => ticketClass?.id === selectedClassId
          )
        )
        ?.map((s: SectionInfo) => s.id);

      setSelectedSectionIds((prev) => {
        if (prev.includes(sectionInfo.id)) {
          return prev.filter((id) => !updatedSectionIds?.includes(id));
        }
        return uniq([...prev, ...(updatedSectionIds ?? [])]);
      });
    },
    [disabled, venueMapInfo?.sections]
  );

  const onCtrlSelect = useCallback(
    (sectionInfo: SectionInfo) =>
      !disabled &&
      setSelectedSectionIds((prev) => {
        if (prev.includes(sectionInfo.id)) {
          return prev.filter((id) => id !== sectionInfo.id);
        }
        return [...prev, sectionInfo.id];
      }),
    [disabled]
  );

  const onSingleSelect = useCallback(
    (sectionInfo: SectionInfo) =>
      !disabled &&
      setSelectedSectionIds((prev) => {
        if (prev.includes(sectionInfo.id)) {
          return prev.filter((id) => id !== sectionInfo.id);
        }
        return [...prev, sectionInfo.id];
      }),
    [disabled]
  );

  const onSectionClicked = useCallback(
    (e: MouseEvent, sectionInfo: SectionInfo) => {
      if (disabled) return;
      if (e.shiftKey) {
        onShiftSelect(sectionInfo);
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        onCtrlSelect(sectionInfo);
        return;
      }

      onSingleSelect(sectionInfo);
    },
    [disabled, onSingleSelect, onShiftSelect, onCtrlSelect]
  );

  const onToggleMirrors = useCallback(
    (mirrors: SectionInfo[], exclude?: boolean) => {
      if (disabled) return;
      setSelectedSectionIds((prev) => {
        const mirrorIds = mirrors.map(({ id }) => id);
        if (exclude) {
          return [...prev.filter((id) => !mirrorIds.includes(id))];
        } else {
          return uniq([...prev, ...mirrorIds]);
        }
      });
    },
    [disabled]
  );

  const onSelectedSectionsChange = useCallback(
    (sections: SectionInfo[]) => {
      if (disabled) return;
      setSelectedSectionIds(sections.map(({ id }) => id));
    },
    [disabled]
  );

  return {
    selectedSection,
    selectedSectionIds,
    selectedSectionData,
    onSectionClicked,
    onResetSections,
    onToggleMirrors,
    onSelectedSectionsChange,
  };
};
